/** Delays execution for `ms` milliseconds. */
export function delay(ms: number): Promise<void> {
	return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * {@link delay} whose timer is `unref`'d: the sleep resolves the same way, but it never holds the Node event loop open
 * on its own.
 *
 * THE SHAPE A DEADLINE ARM IN A `Promise.race` NEEDS. When the sleep exists only to bound a real piece of work, the
 * loser of the race is a timer nobody is waiting for -- and a REF'd one keeps the process alive for the rest of its
 * interval after the work already landed. That is a hang in a suite whose process-exit seam is stubbed, and a
 * per-invocation tax in a short-lived CLI.
 *
 * NODE-ONLY, which is why it is a SEPARATE export rather than an option on {@link delay}: `unref` is a member of Node's
 * `Timeout` object and is absent from the DOM/`workerd` `setTimeout`, whose return is a number. Every worker- and
 * browser-side caller keeps the plain {@link delay} and pays no branch.
 *
 * ONE KNOWN NON-ADOPTER, recorded so the next reader does not read it as an oversight: `unrefDelay` in
 * `tools/flowrunner-cli/run-cli.ts` spells this body privately because that module's own header pins its static import
 * head to `node:*` and `@federa/const/*` VALUE imports only (a leaf import there measured +344ms on a blocking
 * hook path), so a `@federa/utils` value import is foreclosed there by contract rather than by preference.
 */
export function delayUnref(ms: number): Promise<void> {
	return new Promise((resolve) => void setTimeout(resolve, ms).unref());
}

/**
 * ONE TURN OF THE EVENT LOOP, WITH NO TIMER: a promise that settles on the next `setImmediate`, so every macrotask queued
 * before it has run when the await returns.
 *
 * TWO USES, ONE HOME (2026-09-24). A suite waits out a detached write or a yielded fold with it; a long synchronous walk
 * yields with it so an engine's step timeout can fire. The walk used `delay(0)` for that until the code-catalog local
 * lane's own phase clock showed the cost: `setTimeout(0)` is clamped to about a millisecond, and to 15.6 ms on the
 * default Windows clock, so ~194 yields over a ~38.8k-entry listing were seconds of pure timer wait. It lives here
 * rather than under `utils/testing/` so production code can reach it.
 *
 * NODE-ONLY, like {@link delayUnref}: `setImmediate` is a Node global.
 */
export function nextMacrotask(): Promise<void> {
	return new Promise((settle) => void setImmediate(settle));
}

/** Synchronous busy-wait for a given duration. Used for file lock retries on Windows where async sleep is not viable. */
export function busyWait(ms: number): void {
	const deadline = Date.now() + ms;
	while (Date.now() < deadline) {
		// Intentional busy-wait
	}
}
